import React, { useEffect, useRef, useState } from 'react';
import {
  MoreHorizontal, Edit2, Trash2, ListTodo, Paperclip, Download,
  Reply as ReplyIcon, Flag, Users, ChevronDown, MessageSquare, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { notesApi } from '../../lib/notesApi';
import { NoteComposer } from './NoteComposer';
import { sanitizeHtml } from './sanitizeHtml';
import './notes-rich-text.css';

const TASK_STATES = [
  { value: 'todo', label: 'To do', badge: 'bg-slate-100 text-slate-700 border-slate-200' },
  { value: 'in_progress', label: 'In progress', badge: 'bg-blue-50 text-blue-700 border-blue-200' },
  { value: 'blocked', label: 'Blocked', badge: 'bg-rose-50 text-rose-700 border-rose-200' },
  { value: 'done', label: 'Done', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
];

function fmtTime(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch (_e) { return iso; }
}

function initials(name) {
  const n = name || '';
  return n.split(/\s+/).filter(Boolean).slice(0, 2).map((s) => (s[0] || '').toUpperCase()).join('') || '?';
}

function stateMeta(v) {
  for (let i = 0; i < TASK_STATES.length; i++) if (TASK_STATES[i].value === v) return TASK_STATES[i];
  return TASK_STATES[0];
}

export function NoteItem({ note, currentUser, accountId, onChanged, repliesProvider, level }) {
  const [editing, setEditing] = useState(false);
  const [replying, setReplying] = useState(false);
  const [busy, setBusy] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const [showReplies, setShowReplies] = useState(false);
  const [replies, setReplies] = useState([]);
  const [loadingReplies, setLoadingReplies] = useState(false);

  const isAuthor = note.author_id === (currentUser && currentUser.id);
  const isAssignee = (note.assignees || []).some((a) => a.user_id === (currentUser && currentUser.id));
  const isOpsAdmin = currentUser && (currentUser.is_super_admin || currentUser.is_ops_admin || currentUser.is_ops_editor);
  const canEditOrDelete = isAuthor && !note.is_deleted;
  const canChangeTask = !note.is_deleted && (isAuthor || isAssignee || isOpsAdmin);
  const meta = stateMeta(note.task_state);
  const isTask = !!note.is_task;

  useEffect(() => {
    if (!menuOpen) return undefined;
    const handler = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  useEffect(() => {
    if (note.is_deleted) return;
    if (!currentUser || !currentUser.id) return;
    const tagged = (note.mentions || []).some((m) => m.user_id === currentUser.id)
      || (note.assignees || []).some((a) => a.user_id === currentUser.id);
    if (!tagged) return;
    notesApi.markViewed(note.id, { account_id: accountId }).catch(() => {});
  }, [note.id, note.is_deleted, accountId, currentUser]);

  if (note.is_deleted) {
    return (
      <div data-testid="notes-item-deleted" className="my-3 rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-2 text-[11px] italic text-slate-400">
        Deleted by {note.deleted_by_name || 'user'} &middot; {fmtTime(note.deleted_at)}
      </div>
    );
  }

  const handleDelete = async () => {
    if (!window.confirm('Delete this note? This cannot be undone.')) return;
    setBusy(true);
    try { await notesApi.remove(note.id, { account_id: accountId }); toast.success('Note deleted'); if (onChanged) onChanged(); }
    catch (e) { toast.error((e && e.response && e.response.data && e.response.data.detail) || 'Could not delete'); }
    finally { setBusy(false); }
  };

  const handleMarkAsTask = async () => {
    setBusy(true);
    try { await notesApi.patchTask(note.id, { is_task: true, task_state: note.task_state || 'todo', assignees: note.assignees || [] }, { account_id: accountId }); toast.success('Marked as task'); if (onChanged) onChanged(); }
    catch (e) { toast.error((e && e.response && e.response.data && e.response.data.detail) || 'Could not mark as task'); }
    finally { setBusy(false); }
  };

  const handleUnmarkTask = async () => {
    if (!window.confirm('Convert this task back to a plain note? Status and assignees will be cleared.')) return;
    setBusy(true);
    try { await notesApi.patchTask(note.id, { is_task: false }, { account_id: accountId }); toast.success('Now a plain note'); if (onChanged) onChanged(); }
    catch (e) { toast.error((e && e.response && e.response.data && e.response.data.detail) || 'Could not unmark'); }
    finally { setBusy(false); }
  };

  const handleStateChange = async (state) => {
    setBusy(true);
    try { await notesApi.patchTask(note.id, { is_task: true, task_state: state }, { account_id: accountId }); if (onChanged) onChanged(); }
    catch (e) { toast.error((e && e.response && e.response.data && e.response.data.detail) || 'Could not change status'); }
    finally { setBusy(false); }
  };

  const handleToggleImportant = async () => {
    setBusy(true);
    try { await notesApi.patchImportant(note.id, !note.is_important, { account_id: accountId }); if (onChanged) onChanged(); }
    catch (e) { toast.error((e && e.response && e.response.data && e.response.data.detail) || 'Could not toggle important'); }
    finally { setBusy(false); }
  };

  const handleEditSubmit = async (body) => {
    await notesApi.update(note.id, { content: body.content, content_html: body.content_html, mentions: body.mentions, attachments: body.attachments }, { account_id: accountId });
    setEditing(false); if (onChanged) onChanged();
  };

  const handleReplySubmit = async (body) => {
    await notesApi.create(Object.assign({}, body, { parent_id: note.id }));
    toast.success('Reply posted');
    setReplying(false);
    if (showReplies && repliesProvider) {
      setLoadingReplies(true);
      try { const list = await repliesProvider(note.id); setReplies(list || []); }
      finally { setLoadingReplies(false); }
    }
    if (onChanged) onChanged();
  };

  const toggleReplies = async () => {
    const next = !showReplies;
    setShowReplies(next);
    if (next && repliesProvider) {
      setLoadingReplies(true);
      try { const list = await repliesProvider(note.id); setReplies(list || []); }
      finally { setLoadingReplies(false); }
    }
  };

  if (editing) {
    return (
      <div data-testid="notes-item-editing" className="my-3 rounded-md border border-teal-200 bg-teal-50/30">
        <NoteComposer
          accountId={accountId}
          pageKey={note.page_key}
          pageLabel={note.page_label}
          initialContent={note.content || ''}
          initialContentHtml={note.content_html || ''}
          initialMentions={note.mentions || []}
          initialAssignees={note.assignees || []}
          initialAttachments={note.attachments || []}
          initialIsTask={!!note.is_task}
          initialTaskState={note.task_state || 'todo'}
          initialIsImportant={!!note.is_important}
          submitLabel="Save"
          editing
          onSubmit={handleEditSubmit}
          onCancel={() => setEditing(false)}
        />
      </div>
    );
  }

  const cardCls = isTask
    ? 'my-3 rounded-md border border-slate-200 bg-white p-3 border-l-4 border-l-indigo-400'
    : 'my-3 rounded-md border border-slate-200 bg-white p-3';
  const indentStyle = level ? { marginLeft: 28 } : undefined;
  const safeHtml = note.content_html ? sanitizeHtml(note.content_html) : '';
  const importantBtnCls = note.is_important
    ? 'rounded p-1 hover:bg-slate-100 text-amber-600'
    : 'rounded p-1 hover:bg-slate-100 text-slate-400 hover:text-amber-600';

  return (
    <div data-testid="notes-item" className={cardCls} style={indentStyle}>
      {isTask && (
        <div className="mb-2 -mt-1 -mx-1 rounded px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-indigo-700 bg-indigo-50/60 flex items-center gap-1">
          <ListTodo className="h-3 w-3" /> Task
        </div>
      )}
      <div className="flex items-start gap-2.5">
        <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-slate-200 text-[10px] font-semibold text-slate-700">
          {initials(note.author_name)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0 truncate text-xs">
              <span className="font-semibold text-slate-900">{note.author_name}</span>
              <span className="ml-1.5 text-slate-400">{fmtTime(note.created_at)}</span>
              {note.edited && (<span className="ml-1.5 text-[10px] italic text-slate-400">(edited)</span>)}
              {note.is_important && (
                <span data-testid="notes-important-badge" className="ml-2 inline-flex items-center gap-0.5 rounded-full bg-amber-50 text-amber-700 px-1.5 py-0.5 text-[10px] font-medium border border-amber-200">
                  <Flag className="h-2.5 w-2.5" fill="currentColor" /> Important
                </span>
              )}
            </div>
            <div className="flex flex-shrink-0 items-center gap-0.5 relative">
              <button type="button" onClick={handleToggleImportant} disabled={busy}
                data-testid="notes-item-important-toggle"
                title={note.is_important ? 'Unmark important' : 'Mark important'}
                className={importantBtnCls}>
                <Flag className="h-3.5 w-3.5" fill={note.is_important ? 'currentColor' : 'none'} />
              </button>
              {!note.parent_id && (
                <button type="button" onClick={() => setReplying((v) => !v)}
                  data-testid="notes-item-reply-btn" title="Reply"
                  className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
                  <ReplyIcon className="h-3.5 w-3.5" />
                </button>
              )}
              <button type="button" onClick={() => setMenuOpen((v) => !v)} disabled={busy}
                data-testid="notes-item-kebab" aria-label="More actions"
                className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
                <MoreHorizontal className="h-3.5 w-3.5" />
              </button>
              {menuOpen && (
                <div ref={menuRef} data-testid="notes-kebab-menu"
                  className="absolute right-0 top-full z-50 mt-1 min-w-[160px] rounded-md border border-slate-200 bg-white py-1 shadow-md">
                  {canEditOrDelete && (
                    <button type="button" onClick={() => { setMenuOpen(false); setEditing(true); }}
                      data-testid="notes-kebab-edit"
                      className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-slate-700 hover:bg-slate-50">
                      <Edit2 className="h-3.5 w-3.5" /> Edit
                    </button>
                  )}
                  {!isTask && canEditOrDelete && (
                    <button type="button" onClick={() => { setMenuOpen(false); handleMarkAsTask(); }}
                      data-testid="notes-kebab-mark-task"
                      className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-slate-700 hover:bg-slate-50">
                      <ListTodo className="h-3.5 w-3.5" /> Mark as task
                    </button>
                  )}
                  {isTask && canChangeTask && (
                    <button type="button" onClick={() => { setMenuOpen(false); handleUnmarkTask(); }}
                      data-testid="notes-kebab-unmark-task"
                      className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-slate-700 hover:bg-slate-50">
                      <ListTodo className="h-3.5 w-3.5" /> Convert to note
                    </button>
                  )}
                  {canEditOrDelete && (
                    <button type="button" onClick={() => { setMenuOpen(false); handleDelete(); }}
                      data-testid="notes-kebab-delete"
                      className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-rose-600 hover:bg-rose-50">
                      <Trash2 className="h-3.5 w-3.5" /> Delete
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="mt-1">
            {safeHtml ? (
              <div className="notes-rt-render text-sm text-slate-800" dangerouslySetInnerHTML={{ __html: safeHtml }} />
            ) : (
              <div className="whitespace-pre-wrap break-words text-sm text-slate-800">{note.content || ''}</div>
            )}
          </div>

          {note.attachments && note.attachments.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="notes-item-attachments">
              {note.attachments.map((a) => (
                <a key={a.key} href={a.url} target="_blank" rel="noreferrer"
                  data-testid="notes-attachment-link" title={a.name}
                  className="inline-flex max-w-[260px] items-center gap-1 rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-[11px] text-slate-700 hover:border-slate-400 hover:bg-slate-100">
                  <Paperclip className="h-3 w-3 text-slate-500" />
                  <span className="truncate">{a.name}</span>
                  <Download className="h-3 w-3 text-slate-400" />
                </a>
              ))}
            </div>
          )}

          {isTask && (
            <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-2" data-testid="notes-task-footer">
              <span className={'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium ' + meta.badge}>
                {meta.label}
              </span>
              {canChangeTask && (
                <div className="relative">
                  <select
                    value={note.task_state || 'todo'}
                    onChange={(e) => handleStateChange(e.target.value)}
                    disabled={busy}
                    data-testid="notes-task-state-dropdown"
                    className="appearance-none rounded-md border border-slate-200 bg-white px-2 py-0.5 pr-6 text-[10px] text-slate-700 focus:border-teal-500 focus:outline-none"
                  >
                    {TASK_STATES.map((s) => (
                      <option key={s.value} value={s.value}>Move to {s.label}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-1 top-1/2 h-3 w-3 -translate-y-1/2 text-slate-400 pointer-events-none" />
                </div>
              )}
              <span className="flex-1" />
              <div className="flex items-center gap-1 text-[11px] text-slate-600">
                <Users className="h-3 w-3 text-slate-400" />
                {(note.assignees || []).length > 0 ? (
                  <span className="inline-flex flex-wrap gap-1">
                    {(note.assignees || []).map((a) => (
                      <span key={a.user_id} className="rounded-full bg-indigo-50 px-1.5 py-0.5 text-[10px] text-indigo-700 border border-indigo-200">{a.name}</span>
                    ))}
                  </span>
                ) : (
                  <span className="italic text-slate-400">Unassigned</span>
                )}
              </div>
              {note.task_state === 'done' && note.completed_by_name && (
                <div className="w-full text-[10px] italic text-emerald-700">
                  Completed by {note.completed_by_name} &middot; {fmtTime(note.completed_at)}
                </div>
              )}
            </div>
          )}

          {!note.parent_id && ((note.reply_count || 0) > 0 || showReplies) && (
            <div className="mt-2">
              <button type="button" onClick={toggleReplies}
                data-testid="notes-item-toggle-replies"
                className="inline-flex items-center gap-1 rounded text-[11px] text-slate-500 hover:text-slate-800">
                <MessageSquare className="h-3 w-3" />
                {showReplies ? 'Hide' : 'Show'} {(note.reply_count || replies.length) || 0} replies
              </button>
              {showReplies && (
                <div className="mt-1.5 border-l-2 border-slate-100 pl-3">
                  {loadingReplies && (
                    <div className="text-[11px] text-slate-400 py-1">
                      <Loader2 className="inline mr-1 h-3 w-3 animate-spin" /> Loading...
                    </div>
                  )}
                  {!loadingReplies && replies.length === 0 && (
                    <div className="text-[11px] text-slate-400 py-1 italic">No replies yet.</div>
                  )}
                  {!loadingReplies && replies.map((rep) => (
                    React.createElement(NoteItem, {
                      key: rep.id,
                      note: rep,
                      currentUser,
                      accountId,
                      onChanged,
                      level: (level || 0) + 1,
                    })
                  ))}
                </div>
              )}
            </div>
          )}

          {replying && (
            <div className="mt-2 rounded-md border border-slate-200 bg-slate-50/40">
              <NoteComposer
                accountId={accountId}
                pageKey={note.page_key}
                pageLabel={note.page_label}
                parentId={note.id}
                submitLabel="Reply"
                editing
                onSubmit={handleReplySubmit}
                onCancel={() => setReplying(false)}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// (The previous ReplyRow wrapper was removed — recursion now happens via
// React.createElement(NoteItem, ...) inline so the visual-edits Babel
// metadata plugin doesn't loop on the self-reference.)

export default NoteItem;
